/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import emailIcon from './email_icon.svg'

/**
 * Props for the CheckYourEmail component
 * @typedef {Object} CheckYourEmailProps
 * @property {string} userEmail - The email address of the user
 * @property {number} timer - Timer for resend email functionality
 * @property {Function} onResendEmail - Function to handle resending email
 */
type CheckYourEmailProps = {
  userEmail: string
  timer: number
  onResendEmail: (email: string) => void
}

/**
 * CheckYourEmail component for the underage invitation flow
 * Displays a message to check email and provides option to resend verification email
 *
 * @param {CheckYourEmailProps} props - Component props
 * @returns {JSX.Element} The rendered component
 */
const CheckYourEmail = ({
  userEmail,
  timer,
  onResendEmail,
}: CheckYourEmailProps) => {
  return (
    <div css={containerStyle}>
      <img alt='Email Icon' src={emailIcon} />
      <Text
        variant='heading2'
        weight='semiBold'
        color={colors.NEUTRAL_0}
        style={'text-align: center;'}
      >
        {capitalizeWords('Check Your Email')}
      </Text>
      <Text
        variant='paragraphMedium'
        weight='regular'
        color={colors.NEUTRAL_50}
        style={'text-align: center;'}
      >
        Great news! We&apos;ve sent a confirmation email to your inbox. All you
        need to do is click the link inside to activate your account.
      </Text>
      <span onClick={() => (timer > 0 ? {} : onResendEmail(userEmail))}>
        <Text
          variant='paragraphMedium'
          weight='regular'
          style={`
            cursor: ${timer > 0 ? 'not-allowed' : 'pointer'};
            text-align: center;
            color: ${timer > 0 ? colors.ATTENDANCE_GRAY : colors.NEUTRAL_0};
            text-decoration: underline;
            text-underline-offset: 2px;
          `}
        >
          Resend email {timer > 0 && ` (${timer})`}
        </Text>
      </span>
    </div>
  )
}

export default CheckYourEmail

// ---- styled ----
/*
 * A status screen with nothing to fill in, so it centers as a unit. The <br />
 * that hard-wrapped the message is gone — the column's width sets the wrap.
 */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`
